import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import db from '../firebase/firestore';
import { useAuth } from '../auth/useAuth';
import { EVENT_AUDIENCE } from '../utils/membership';
import { maySeeTestData, visibleEvents } from '../utils/testData';

/**
 * Termine live aus Firestore.
 *
 * WICHTIG: Firestore prüft die Security Rules pro Dokument und weist jede
 * Abfrage ab, die potenziell nicht lesbare Treffer liefern könnte. Die
 * öffentliche Liste MUSS deshalb where('audiences','array-contains','public')
 * enthalten - ohne den Filter scheitert sie mit permission-denied, obwohl
 * die öffentlichen Termine einzeln lesbar wären.
 *
 * Dieselbe Mechanik trägt das Rollenmodell: Ein angemeldetes Mitglied fragt
 * mit seiner EIGENEN Zugehörigkeit ab (`audience`), und damit ist jeder
 * Treffer für es lesbar. Genau deshalb steht in `audiences` die
 * ausgeschriebene Liste und nicht die niedrigste Stufe - eine Rangfolge
 * ließe sich nicht abfragen.
 *
 * `from`/`to` grenzen das geladene Zeitfenster ein. Ohne sie kommt die ganze
 * Historie mit - richtig für die Terminseite (zeigt "Vergangene Termine"), den
 * Kalender (man kann zurückblättern) und den Editor (muss Altes bearbeiten
 * können), unnötig für die beiden Teaser, die ohnehin nur Kommendes zeigen.
 * Der Filter ersetzt die clientseitige isUpcoming-Prüfung nicht, er spart nur
 * Daten - mit `startOfToday()` als `from` schneidet er nichts weg, was die
 * Anzeige noch zeigen würde (beide ziehen die Grenze am Tagesbeginn).
 *
 * @param {object}  options
 * @param {boolean} options.includeMembers  true = auch interne Termine (nur für Angemeldete)
 * @param {string|null} options.audience    eigene Zugehörigkeit; null = ohne Filter,
 *                                          erlaubt nur für Admins und Organisatoren
 * @param {number}  options.limitTo         optionale Begrenzung für Teaser-Ansichten
 * @param {Date|number|null} options.from   frühester Termin (>=), null = ohne Grenze
 * @param {Date|number|null} options.to     spätester Termin (<=), null = ohne Grenze
 * @param {boolean} options.includeTestData true = Testtermine bleiben in der Liste
 *
 * **Testtermine sind ueberall ausgeblendet** (`isTestData`, siehe
 * `utils/testData.js`) - ausser fuer das Testkonto selbst und dort, wo
 * `includeTestData` ausdruecklich gesetzt ist. Das ist genau EINE Stelle: der
 * Termin-Reiter im Admin-Bereich, damit Testtermine auffindbar und loeschbar
 * bleiben. Im Kalender und auf der Uebersicht sieht auch ein Admin sie nicht -
 * sonst waere die Ausblendung fuer den, der sie am haeufigsten anschaut,
 * wirkungslos.
 */
export function useEvents({
  includeMembers = false,
  audience = null,
  limitTo = null,
  from = null,
  to = null,
  includeTestData = false,
} = {}) {
  const { profile } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auf Millisekunden herunterbrechen, BEVOR es ins Dependency-Array geht:
  // `from={new Date()}` am Aufrufort ist bei jedem Render ein neues Objekt.
  // Als Abhängigkeit würde der onSnapshot-Listener damit endlos ab- und wieder
  // angemeldet. Eine Zahl vergleicht React dagegen per Wert.
  const fromMs = from == null ? null : +from;
  const toMs = to == null ? null : +to;

  useEffect(() => {
    // Ohne Firebase-Konfiguration bleibt die Liste leer statt zu krachen -
    // die Seite zeigt dann ihren Leerzustand. Siehe firebase/config.js.
    if (!db) {
      setLoading(false);
      return undefined;
    }

    // Index (audiences CONTAINS, startAt ASC) deckt beide gefilterten Zweige
    // ab; der ungefilterte Admin-/Organisator-Zweig kommt mit dem
    // automatischen Einzelfeld-Index aus.
    // Nicht `window` nennen - das ueberschattete das globale window-Objekt.
    const range = [];
    if (fromMs != null) range.push(where('startAt', '>=', new Date(fromMs)));
    if (toMs != null) range.push(where('startAt', '<=', new Date(toMs)));

    // Ohne Anmeldung zählt nur, was öffentlich steht. Angemeldete fragen mit
    // ihrer Zugehörigkeit ab; null heißt "alles" und ist Admins und
    // Organisatoren vorbehalten (siehe firestore.rules).
    const filter = includeMembers ? audience : EVENT_AUDIENCE.PUBLIC;

    const base = collection(db, 'events');
    const q = filter
      ? query(
          base,
          where('audiences', 'array-contains', filter),
          ...range,
          orderBy('startAt', 'asc')
        )
      : query(base, ...range, orderBy('startAt', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [includeMembers, audience, fromMs, toMs]);

  // ZUERST die Testtermine heraus, DANN kappen: Andersherum verbrauchte ein
  // Testtermin einen Platz im Teaser und `total` zaehlte ihn mit.
  const ohneTest = visibleEvents(events, maySeeTestData(profile, includeTestData));
  const visible = limitTo ? ohneTest.slice(0, limitTo) : ohneTest;

  return { events: visible, total: ohneTest.length, loading, error };
}

/**
 * Ein einzelner Termin für die Detailseite.
 *
 * `notFound` ist ein eigener Zustand und nicht einfach `event === null`:
 * Links auf gelöschte Termine wird es geben (der Mitgliederbereich verschickt
 * sie im Gespräch weiter), und "Diesen Termin gibt es nicht mehr" ist eine
 * andere Aussage als "wird noch geladen".
 */
export function useEvent(eventId) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!db || !eventId) {
      setLoading(false);
      setNotFound(!eventId);
      return undefined;
    }

    setLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, 'events', eventId),
      (snap) => {
        setEvent(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setNotFound(!snap.exists());
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [eventId]);

  return { event, loading, notFound, error };
}
